// Partner onboarding tasks template
// This defines the complete list of onboarding tasks for partners

export interface OnboardingTask {
  id: string;
  title: string;
  description: string;

  link?: string; //for modal link at the bottom of video
  required: boolean;
  isWhiteLabel: boolean;
  completed: boolean;


  //content for tutorials modal. Dynamic sections with title, paragraphs, video url. array of paragraphs means that each string will start on a new line.
  //Order of elements will be like title --> paragraphsTop --> videoUrl --> paragraphsBottom.
  modalSections?: {
    title?: string;
    paragraphsTop?: string[]; 
    paragraphsBottom?: string[];
    videoUrl?: string;
  }[];
}

export const PARTNER_ONBOARDING_TASKS: OnboardingTask[] = [
  {
    id: 'setup-custom-domain',
    title: 'Set up custom domain',
    description: 'Configure your custom domain for white-label experience',
    link: '/partner/domains',
    required: true,
    isWhiteLabel: true,
    completed: false,
    modalSections: [
      {
        title: 'Custom Domain Setup',
        paragraphsTop: [
          'Configure your custom domain to provide a seamless white-label experience for your clients.',
          'This step is essential for maintaining your brand identity throughout the platform.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
        paragraphsBottom: [
          'Your clients will access the platform through your domain instead of our default URL.',
          'Make sure you have DNS access to your domain before proceeding.',
          'The domain setup process typically takes 24-48 hours to fully propagate.'
        ],
      },
    ],
  },
  {
    id: 'configure-domain-email',
    title: 'Configure email for domain',
    description: 'Set up email authentication and sending for your custom domain',
    link: '/partner/domains',
    required: true,
    isWhiteLabel: true,
    completed: false,
    modalSections: [
      {
        title: 'Domain Email Configuration',
        paragraphsTop: [
          'Configure email authentication (SPF, DKIM, DMARC) for your custom domain.',
          'This ensures emails sent from the platform appear legitimate and professional.'
        ],
        paragraphsBottom: [
          'Proper email authentication prevents emails from being marked as spam.',
          'You will need access to your domain\'s DNS settings to add the required records.',
          'This step is crucial for maintaining email deliverability and trust.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  {
    id: 'setup-branding',
    title: 'Set up your branding',
    description: 'Configure your logo, colors, and company name to match your brand',
    link: '/partner/branding#colors',
    required: true,
    isWhiteLabel: true,
    completed: false,
    modalSections: [
      {
        title: 'Branding Configuration',
        paragraphsTop: [
          'Configure your branding elements to create a consistent white-label experience.',
          'Upload your logo, set brand colors, and customize your company information.'
        ],
        paragraphsBottom: [
          'Your branding will appear throughout the platform for your clients.',
          'Make sure your logo is high quality and your colors match your brand guidelines.',
          'You can update these settings anytime as your brand evolves.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  {
    id: 'configure-basic-copy',
    title: 'Configure basic copy and brand messages',
    description: 'Set up your company text content, messages, and copy for the platform',
    link: '/partner/branding#copy',
    required: true,
    isWhiteLabel: true,
    completed: false,
    modalSections: [
      {
        title: 'Basic Copy Configuration',
        paragraphsTop: [
          'Configure all text content that will appear throughout your white-label platform.',
          'This includes company information, welcome messages, and user-facing text.'
        ],
        paragraphsBottom: [
          'Set up your company name, description, and contact information.',
          'Customize welcome messages, error messages, and call-to-action buttons.',
          'All text will be displayed to your clients instead of the original platform text.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Brand Messages Setup',
        paragraphsTop: [
          'Create consistent brand messaging across all user touchpoints.',
          'Define your tone of voice, key messages, and communication style.'
        ],
        paragraphsBottom: [
          'This helps maintain brand consistency in all automated communications.',
          'Consider your target audience when crafting these messages.',
          'You can update these messages anytime to reflect brand changes.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  {
    id: 'connect-stripe',
    title: 'Connect Stripe payment',
    description: 'Set up Stripe integration to process client payments and subscriptions',
    link: '/partner/payouts',
    required: true,
    isWhiteLabel: true,
    completed: false,
    modalSections: [
      {
        title: 'Stripe Integration Setup',
        paragraphsTop: [
          'Connect your Stripe account to enable payment processing for your clients.',
          'This integration handles all billing, subscriptions, and payment transactions securely.'
        ],
        paragraphsBottom: [
          'You will need a Stripe account and API keys to complete this setup.',
          'Stripe handles all payment security, compliance, and fraud protection.',
          'Test the integration thoroughly in Stripe\'s test mode before going live.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Payment Configuration',
        paragraphsTop: [
          'Configure your payment settings, currencies, and billing preferences.',
          'Set up webhook endpoints to receive real-time payment notifications.'
        ],
        paragraphsBottom: [
          'Choose which payment methods to accept (cards, bank transfers, etc.).',
          'Configure your payout schedule and tax settings.',
          'Set up automatic retry logic for failed payments.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
//copy item for co-branded partners
  {
    id: 'connect-stripe',
    title: 'Connect Stripe payment',
    description: 'Set up Stripe integration to process client payments and subscriptions',
    link: '/partner/payouts',
    required: true,
    isWhiteLabel: false,
    completed: false,
    modalSections: [
      {
        title: 'Stripe Integration Setup',
        paragraphsTop: [
          'Connect your Stripe account to enable payment processing for your clients.',
          'This integration handles all billing, subscriptions, and payment transactions securely.'
        ],
        paragraphsBottom: [
          'You will need a Stripe account and API keys to complete this setup.',
          'Stripe handles all payment security, compliance, and fraud protection.',
          'Test the integration thoroughly in Stripe\'s test mode before going live.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Payment Configuration',
        paragraphsTop: [
          'Configure your payment settings, currencies, and billing preferences.',
          'Set up webhook endpoints to receive real-time payment notifications.'
        ],
        paragraphsBottom: [
          'Choose which payment methods to accept (cards, bank transfers, etc.).',
          'Configure your payout schedule and tax settings.',
          'Set up automatic retry logic for failed payments.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  {
    id: 'configure-custom-pricing',
    title: 'Configure custom pricing plans',
    description: 'Set up your custom pricing tiers and subscription plans for clients',
    link: '/partner/pricing',
    required: true,
    isWhiteLabel: true,
    completed: false,
    modalSections: [
      {
        title: 'Pricing Plans Creation',
        paragraphsTop: [
          'Create custom pricing plans that your clients can subscribe to.',
          'Set up different tiers with various features and pricing options to match your business model.'
        ],
        paragraphsBottom: [
          'Pricing plans determine how much your clients will pay for the service.',
          'You can create multiple tiers (Basic, Pro, Enterprise) with different features and limits.',
          'Consider your costs, profit margins, and market positioning when setting prices.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Feature Configuration',
        paragraphsTop: [
          'Define which features are included in each pricing tier.',
          'Set limits for usage, users, storage, or other resources per plan.'
        ],
        paragraphsBottom: [
          'Clear feature differentiation helps clients choose the right plan.',
          'Consider offering add-ons or upgrades for additional revenue.',
          'Test your pricing strategy with a small group before launching widely.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  {
    id: 'setup-boost-packs',
    title: 'Set up boost packs',
    description: 'Configure boost packs and add-ons for additional revenue streams',
    link: '/partner/pricing',
    required: false,
    isWhiteLabel: true,
    completed: false,
    modalSections: [
      {
        title: 'Boost Packs Configuration',
        paragraphsTop: [
          'Create boost packs and add-ons to increase your revenue per client.',
          'Boost packs are additional features or credits that clients can purchase beyond their base plan.'
        ],
        paragraphsBottom: [
          'Examples: Extra API calls, premium support, additional storage, or advanced features.',
          'Boost packs can be one-time purchases or recurring add-ons.',
          'This creates additional revenue streams and increases client lifetime value.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Pricing Strategy',
        paragraphsTop: [
          'Set competitive prices for your boost packs based on value provided.',
          'Consider offering bundles or discounts for multiple boost pack purchases.'
        ],
        paragraphsBottom: [
          'Test different pricing strategies to find what works best for your market.',
          'Monitor usage patterns to optimize boost pack offerings.',
          'Regularly update boost packs based on client feedback and market demand.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  {
    id: 'explore-tutorials',
    title: 'Explore tutorials',
    description: 'Learn how to use the platform effectively through our comprehensive tutorial library',
    link: '/partner/tutorials',
    required: true,
    isWhiteLabel: false,
    completed: false,
    modalSections: [
      {
        title: 'Tutorial Library Overview',
        paragraphsTop: [
          'Access our comprehensive library of step-by-step tutorials and video guides.',
          'Learn everything from basic setup to advanced features and best practices.'
        ],
        paragraphsBottom: [
          'Tutorials are organized by category and difficulty level for easy navigation.',
          'Each tutorial includes both written instructions and video demonstrations.',
          'Complete tutorials to unlock advanced features and maximize your success.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Key Tutorial Categories',
        paragraphsTop: [
          'Getting Started: Basic platform navigation and initial setup.',
          'Client Management: How to create, manage, and support your clients.',
          'White Label Setup: Customize the platform with your branding.'
        ],
        paragraphsBottom: [
          'Advanced Features: Leverage powerful tools for maximum efficiency.',
          'Troubleshooting: Common issues and how to resolve them quickly.',
          'Best Practices: Proven strategies from successful partners.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Learning Path Recommendations',
        paragraphsTop: [
          'Start with the "Platform Basics" series to understand core functionality.',
          'Move on to "Client Onboarding" tutorials to learn how to set up your first clients.',
          'Explore "Advanced Configuration" when you\'re ready to customize everything.'
        ],
        paragraphsBottom: [
          'Bookmark tutorials you reference frequently for quick access.',
          'Complete the certification program to become a platform expert.',
          'Share relevant tutorials with your clients to help them succeed.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  {
    id: 'visit-help-center',
    title: 'Visit help center',
    description: 'Explore partner resources, documentation, and support materials',
    link: '/partner/help-center',
    required: false,
    isWhiteLabel: true,
    completed: false,
    modalSections: [
      {
        title: 'Partner Help Center',
        paragraphsTop: [
          'Explore our comprehensive help center designed specifically for partners.',
          'Find guides, tutorials, best practices, and troubleshooting resources.'
        ],
        paragraphsBottom: [
          'The help center contains everything you need to succeed as a partner.',
          'Bookmark important resources for quick reference.',
          'Contact support if you can\'t find what you\'re looking for.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Partner Resources',
        paragraphsTop: [
          'Access marketing materials, case studies, and success stories.',
          'Download logos, templates, and other assets for your business.'
        ],
        paragraphsBottom: [
          'Use these resources to promote your partnership effectively.',
          'Stay updated with the latest product announcements and features.',
          'Join our partner community for networking and knowledge sharing.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  {
    id: 'explore-subscription-section',
    title: 'Explore subscription management',
    description: 'Familiarize yourself with the subscription and billing management features',
    link: '/partner/pricing',
    required: true,
    isWhiteLabel: false,
    completed: false,
    modalSections: [
      {
        title: 'Subscription Management Overview',
        paragraphsTop: [
          'Learn how to manage your clients\' subscriptions and billing through our comprehensive dashboard.',
          'This section provides you with full control over your clients\' accounts, usage, and payments.'
        ],
        paragraphsBottom: [
          'Monitor subscription status, usage analytics, and billing history for all your clients.',
          'Set up automated billing, handle upgrades/downgrades, and manage payment issues.',
          'Access detailed reports to track your revenue and client engagement metrics.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Key Features to Explore',
        paragraphsTop: [
          'Subscription Dashboard: Overview of all client subscriptions and their status.',
          'Usage Analytics: Track how your clients are using the platform and identify opportunities.',
          'Billing Management: Handle payments, refunds, and subscription changes.'
        ],
        paragraphsBottom: [
          'Client Communication: Send billing notifications and manage payment reminders.',
          'Revenue Tracking: Monitor your earnings and commission from each client.',
          'Automated Workflows: Set up rules for handling common billing scenarios.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  {
    id: 'create-first-client',
    title: 'Create and invite your first client',
    description: 'Set up your first sub-account and invite them to the platform',
    link: '/partner/sub-accounts',
    required: true,
    isWhiteLabel: true,
    completed: false,
    modalSections: [
      {
        title: 'Client Creation Process',
        paragraphsTop: [
          'Create your first sub-account to start serving clients.',
          'Set up client details, assign them to a pricing plan, and configure their access.'
        ],
        paragraphsBottom: [
          'Choose the appropriate pricing plan for your client\'s needs.',
          'Set up their login credentials and send them an invitation.',
          'Monitor their usage and provide support as needed.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Client Onboarding',
        paragraphsTop: [
          'Help your client get started with their new account.',
          'Provide them with training materials and support resources.'
        ],
        paragraphsBottom: [
          'Schedule a demo or training session to ensure they understand the platform.',
          'Set up regular check-ins to monitor their progress and satisfaction.',
          'Gather feedback to improve your service and identify upsell opportunities.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },
  
//copy for co-branded partners
  {
    id: 'create-first-client',
    title: 'Create and invite your first client',
    description: 'Set up your first sub-account and invite them to the platform',
    link: '/partner/sub-accounts',
    required: true,
    isWhiteLabel: false,
    completed: false,
    modalSections: [
      {
        title: 'Client Creation Process',
        paragraphsTop: [
          'Create your first sub-account to start serving clients.',
          'Set up client details, assign them to a pricing plan, and configure their access.'
        ],
        paragraphsBottom: [
          'Choose the appropriate pricing plan for your client\'s needs.',
          'Set up their login credentials and send them an invitation.',
          'Monitor their usage and provide support as needed.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
      {
        title: 'Client Onboarding',
        paragraphsTop: [
          'Help your client get started with their new account.',
          'Provide them with training materials and support resources.'
        ],
        paragraphsBottom: [
          'Schedule a demo or training session to ensure they understand the platform.',
          'Set up regular check-ins to monitor their progress and satisfaction.',
          'Gather feedback to improve your service and identify upsell opportunities.'
        ],
        videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
      },
    ],
  },

  


  // {
  //   id: 'example-task',
  //   title: 'Title for task and modal',
  //   description: 'Description for task and modal',

  //   modalSections: [
  //     {
  //       title: 'Title 1',
  //       paragraphsTop: [
  //         'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum nec nunc vel urna malesuada tincidunt. Cras ac justo eget erat finibus dapibus.',
  //         'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium. Nemo enim ipsam voluptatem quia voluptas sit aspernatur.',
  //         'Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam. Quis autem vel eum iure reprehenderit qui in ea voluptate velit.',
  //         'At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores.'
  //       ],
  //       paragraphsBottom: [
  //         'Nunc viverra imperdiet enim, in dignissim est laoreet ac. Pellentesque in justo finibus, viverra elit in, maximus massa.',
  //         'Integer posuere erat a ante venenatis dapibus posuere velit aliquet. Duis mollis, est non commodo luctus.',
  //         'Curabitur blandit tempus porttitor. Maecenas faucibus mollis interdum. Donec ullamcorper nulla non metus auctor fringilla.',
  //         'Praesent commodo cursus magna, vel scelerisque nisl consectetur et. Donec id elit non mi porta gravida at eget metus.'
  //       ],
  //       videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
  //     },
  //     {
  //       title: 'Title 1',
  //       paragraphsTop: [
  //         'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum nec nunc vel urna malesuada tincidunt. Cras ac justo eget erat finibus dapibus.',
  //         'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium. Nemo enim ipsam voluptatem quia voluptas sit aspernatur.',
  //         'Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam. Quis autem vel eum iure reprehenderit qui in ea voluptate velit.',
  //         'At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores.'
  //       ],
  //       paragraphsBottom: [
  //         'Nunc viverra imperdiet enim, in dignissim est laoreet ac. Pellentesque in justo finibus, viverra elit in, maximus massa.',
  //         'Integer posuere erat a ante venenatis dapibus posuere velit aliquet. Duis mollis, est non commodo luctus.',
  //         'Curabitur blandit tempus porttitor. Maecenas faucibus mollis interdum. Donec ullamcorper nulla non metus auctor fringilla.',
  //         'Praesent commodo cursus magna, vel scelerisque nisl consectetur et. Donec id elit non mi porta gravida at eget metus.'
  //       ],
  //       videoUrl: 'https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95',
  //     },
 
  //   ],

  //   link: '/partner/stripe',
  //   required: true,
  //   completed: false,
  //   isWhiteLabel: false,
  // },
];


// Helper function to calculate completion percentage
export function calculateCompletionPercentage(progress: Record<string, { completed: boolean }>) {
  const totalTasks = PARTNER_ONBOARDING_TASKS.length;
  const completedTasks = PARTNER_ONBOARDING_TASKS.filter(
    task => progress[task.id]?.completed
  ).length;
  
  return Math.round((completedTasks / totalTasks) * 100);
}

